import { useState } from 'react';
import { useApi } from '../api.js';
import DataTable from './datatable/DataTable.jsx';

const money = (n) => '$' + Math.round(n ?? 0).toLocaleString();
const moneyK = (n) => '$' + ((n ?? 0) / 1000).toFixed(1) + 'K';

const PERIODS = [
  { value: 'fy-analysis', label: 'FY analysis — this FY YTD vs full prior FY' },
  { value: 'mtd',         label: 'Month to date (vs prior month)' },
  { value: 'lastmonth',   label: 'Last completed month (vs the month before)' },
  { value: 'qtd',         label: 'Quarter to date (vs prior quarter)' },
  { value: 'ytd',         label: 'Year to date (vs prior YTD)' },
  { value: 'trailing12',  label: 'Trailing 12 months (vs months 13–24)' }
];

function Delta({ value, suffix = '%', invert = false }) {
  if (value == null || isNaN(value)) return <span className="small">—</span>;
  const up = invert ? value < 0 : value > 0;
  const cls = up ? 'up' : (value === 0 ? '' : 'down');
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '';
  const sign = value > 0 ? '+' : '';
  return <span className={cls}>{arrow} {sign}{value.toFixed(1)}{suffix}</span>;
}

function PaceBadge({ pace }) {
  if (pace == null) return <span className="small">—</span>;
  const pct = (pace * 100).toFixed(0) + '%';
  let bg = '#f3f4f6', fg = '#374151';
  if (pace >= 1.25) { bg = '#fecaca'; fg = '#7f1d1d'; }
  else if (pace >= 1.05) { bg = '#fee2e2'; fg = '#991b1b'; }
  else if (pace <= 0.75) { bg = '#bbf7d0'; fg = '#14532d'; }
  else if (pace <= 0.95) { bg = '#dcfce7'; fg = '#166534'; }
  return <span style={{ background: bg, color: fg, padding: '1px 8px', borderRadius: 4, fontWeight: 600, fontSize: '0.8rem' }}>{pct}</span>;
}

export default function VendorSpend() {
  const [period, setPeriod] = useState('fy-analysis');
  const [search, setSearch] = useState('');
  const { data, error, loading, refresh } = useApi('/api/vendor-spend?period=' + period);

  if (loading && !data) return <div className="loading">Loading vendor spend…</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return null;

  const { summary = {}, vendors = [], currentPeriod = {}, priorPeriod = {}, periodLabel, analytics } = data;
  const isAnalysis = !!analytics;

  const filtered = vendors.filter(v => !search || v.vendor.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'0.5rem' }}>
        <div>
          <h2 style={{margin:0}}>Vendor Spend — {periodLabel}</h2>
          <div className="small" style={{ marginTop: '0.2rem' }}>
            Current: <strong>{currentPeriod.start} → {currentPeriod.end}</strong>
            {' '}· Prior: <strong>{priorPeriod.start} → {priorPeriod.end}</strong>.
            {' '}Pulled from QuickBooks (Vendor Expenses report).
          </div>
        </div>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <select className="refresh-btn" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ minWidth: 280 }}>
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="refresh-btn" onClick={refresh}>Refresh</button>
        </div>
      </div>

      {isAnalysis ? (
        <>
          <div className="kpi-grid" style={{marginTop:'0.8rem'}}>
            <div className="kpi">
              <div className="lbl">FY{analytics.priorFY} full spend</div>
              <div className="val">{money(summary.priorTotal)}</div>
              <div className="delta"><span style={{color:'#999'}}>${(analytics.priorMonthlyAvgTotal ?? 0).toLocaleString()}/mo avg</span></div>
            </div>
            <div className="kpi">
              <div className="lbl">FY{analytics.currentFY} YTD spend</div>
              <div className="val">{money(summary.total)}</div>
              <div className="delta"><span style={{color:'#999'}}>{analytics.daysElapsed} days in ({analytics.monthsElapsed.toFixed(1)} mo)</span></div>
            </div>
            <div className="kpi">
              <div className="lbl">Annualized at current rate</div>
              <div className="val">{money(analytics.annualizedCurrentTotal)}</div>
              <div className="delta">
                {summary.priorTotal > 0
                  ? <Delta value={(analytics.annualizedCurrentTotal / summary.priorTotal - 1) * 100} invert={true} />
                  : <span className="small">—</span>}
                <span style={{color:'#999'}}> vs FY{analytics.priorFY} actual</span>
              </div>
            </div>
            <div className="kpi">
              <div className="lbl">Overall pace</div>
              <div className="val"><PaceBadge pace={analytics.overallPace} /></div>
              <div className="delta"><span style={{color:'#999'}}>vs expected ${(analytics.expectedToDateTotal ?? 0).toLocaleString()}</span></div>
            </div>
          </div>

          <div className="chart-card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem' }}>
              <h3 style={{margin:0}}>By vendor — FY analysis</h3>
              <input className="refresh-btn" placeholder="Search vendor…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 220 }} />
            </div>
            <DataTable
              viewName="vendorspend.fy-analysis"
              data={filtered}
              emptyMessage="No vendor activity matches."
              defaultSort={[{ id: 'amount', desc: true }]}
              columns={[
                { id: 'vendor', accessorKey: 'vendor', header: 'Vendor', size: 260 },
                { id: 'priorAmount', accessorKey: 'priorAmount', header: 'FY' + analytics.priorFY + ' total', size: 120, meta: { align: 'right' }, cell: ({ getValue }) => money(getValue()) },
                { id: 'priorMonthlyAvg', accessorKey: 'priorMonthlyAvg', header: 'FY' + analytics.priorFY + ' mo avg', size: 120, meta: { align: 'right' }, cell: ({ getValue }) => <span style={{color:'#666'}}>{money(getValue())}</span> },
                { id: 'amount', accessorKey: 'amount', header: 'FY' + analytics.currentFY + ' YTD', size: 120, meta: { align: 'right' }, cell: ({ getValue }) => <strong>{money(getValue())}</strong> },
                { id: 'currentMonthlyAvg', accessorKey: 'currentMonthlyAvg', header: 'YTD mo avg', size: 120, meta: { align: 'right' }, cell: ({ getValue }) => <span style={{color:'#666'}}>{money(getValue())}</span> },
                { id: 'annualizedCurrent', accessorKey: 'annualizedCurrent', header: 'Annualized', size: 110, meta: { align: 'right' }, cell: ({ getValue }) => money(getValue()) },
                { id: 'pace', accessorKey: 'pace', header: 'Pace', size: 90, meta: { align: 'right' }, cell: ({ getValue }) => <PaceBadge pace={getValue()} /> },
                { id: 'runRateDelta', accessorKey: 'runRateDelta', header: 'Run-rate Δ', size: 110, meta: { align: 'right' }, cell: ({ getValue }) => <Delta value={getValue()} invert={true} /> },
              ]}
            />
            {filtered.length > 0 && (
              <div className="card" style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#f8fafc', fontSize: '0.9em' }}>
                <strong>Totals</strong> · FY{analytics.priorFY} total <strong>{money(summary.priorTotal)}</strong> ({money(analytics.priorMonthlyAvgTotal)}/mo)
                · FY{analytics.currentFY} YTD <strong>{money(summary.total)}</strong> ({money(summary.total / Math.max(0.01, analytics.monthsElapsed))}/mo)
                · Annualized <strong>{money(analytics.annualizedCurrentTotal)}</strong>
                · Pace <PaceBadge pace={analytics.overallPace} />
                {summary.priorTotal > 0 && <> · Run-rate Δ <Delta value={(analytics.annualizedCurrentTotal / summary.priorTotal - 1) * 100} invert={true} /></>}
              </div>
            )}
          </div>

          <div className="small" style={{marginTop:'0.6rem'}}>
            <strong>Pace</strong> = YTD spend ÷ (prior FY total × fraction of year elapsed). 100% means we're tracking exactly with last year's rate; below 100% means we're spending slower (green); above 100% means faster (red — bigger numbers are worse for cost control). <strong>Annualized</strong> = current YTD scaled up to a full 365 days. <strong>Run-rate Δ</strong> = how the annualized projection compares to prior FY actual.
          </div>
        </>
      ) : (
        <>
          <div className="kpi-grid" style={{marginTop:'0.8rem'}}>
            <div className="kpi">
              <div className="lbl">Total spend</div>
              <div className="val">{money(summary.total)}</div>
              <div className="delta">
                <Delta value={summary.totalDeltaPct} invert={true} />
                <span style={{color:'#999'}}> vs {money(summary.priorTotal)} prior</span>
              </div>
            </div>
            <div className="kpi">
              <div className="lbl">Active vendors</div>
              <div className="val">{summary.vendorCount ?? 0}</div>
              <div className="delta"><span style={{color:'#999'}}>vs {summary.priorVendorCount ?? 0} prior</span></div>
            </div>
            <div className="kpi">
              <div className="lbl">Average per vendor</div>
              <div className="val">{money(summary.avgPerVendor)}</div>
              <div className="delta"><span style={{color:'#999'}}>among active vendors</span></div>
            </div>
            <div className="kpi">
              <div className="lbl">Top-5 concentration</div>
              <div className="val">{(summary.top5Share ?? 0).toFixed(1)}%</div>
              <div className="delta"><span style={{color:'#999'}}>share of total spend</span></div>
            </div>
          </div>

          <div className="chart-card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem' }}>
              <h3 style={{margin:0}}>By vendor — {periodLabel}</h3>
              <input className="refresh-btn" placeholder="Search vendor…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 220 }} />
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{width: 32}}>#</th>
                  <th>Vendor</th>
                  <th className="num">Spend</th>
                  <th className="num">Prior</th>
                  <th className="num">Δ</th>
                  <th className="num">Δ%</th>
                  <th className="num">% of total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v, i) => (
                  <tr key={v.vendor}>
                    <td className="small" style={{color:'#888'}}>{i + 1}</td>
                    <td>{v.vendor}</td>
                    <td className="num"><strong>{money(v.amount)}</strong></td>
                    <td className="num" style={{color:'#888'}}>{money(v.priorAmount)}</td>
                    <td className="num"><Delta value={v.delta} suffix="" invert={true} /></td>
                    <td className="num"><Delta value={v.deltaPct} invert={true} /></td>
                    <td className="num">{summary.total > 0 ? ((v.amount / summary.total) * 100).toFixed(1) + '%' : '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="7" className="note">No vendor spend in this period.</td></tr>
                )}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #94a3b8' }}>
                    <td></td>
                    <td><strong>Total</strong></td>
                    <td className="num"><strong>{money(summary.total)}</strong></td>
                    <td className="num" style={{color:'#888'}}>{money(summary.priorTotal)}</td>
                    <td className="num"><Delta value={summary.totalDelta} suffix="" invert={true} /></td>
                    <td className="num"><Delta value={summary.totalDeltaPct} invert={true} /></td>
                    <td className="num">100%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </>
  );
}
